/**
 * Unified Markup Viewer Component
 *
 * A read-only document viewer with highlight support, built on the unified
 * ProseMirror Editor infrastructure. This component replaces the TipTap-based
 * MarkupViewer with a more efficient implementation.
 *
 * Features:
 * - Read-only document viewing with highlights
 * - Tooltip display for hovered/selected highlights
 * - Teacher comment add/edit/delete functionality
 * - Text selection for adding new teacher comments
 * - Keyboard navigation via sidebar selection
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Plugin } from "@tiptap/pm/state";

import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/Modal";
import { buttonVariants } from "@markeroo/theme";
import { ui, confirmations } from "@/strings";
import type { NotificationKind } from "@/providers/NotificationProvider";
import type { ShareTag, ShareTagDefinition, TeacherComment } from "@markeroo/shared";
import { mutedText } from "@/styles/variants";
import { useDeleteHighlight, useDeleteTeacherComment } from "@/api/hooks";
import { useNotification } from "@/providers/NotificationProvider";
import { notifications } from "@/strings";
import { AddCommentModal } from "../components/AddCommentModal";
import { EditCommentModal } from "../components/EditCommentModal";
import { extractContext, getPlainTextFromTipTap, getContainingSentenceInfo } from "@/utils/sentenceDetection";
import { pmPositionToPlainTextOffset } from "@/utils/rangeToOffsets";
import { useAuthStore } from "@/stores/auth";
import PiCheckTickCircleSolid from "@markeroo/icons/src/solid/general/PiCheckTickCircleSolid";
import PiPencilEditStroke from "@markeroo/icons/src/stroke/general/PiPencilEditStroke";
import PiDeleteDustbin01Stroke from "@markeroo/icons/src/stroke/general/PiDeleteDustbin01Stroke";
import { Spinner } from "@/components/Spinner";
import { Tooltip } from "@/components/Tooltip";

import { Editor, type EditorRef } from "./Editor";
import {
  createPositionedPhrasePlugin,
  setPositionedPhrases,
} from "./plugins/positionedPhrasePlugin";
import {
  createHighlightDecorationPlugin,
  type HoveredHighlight,
} from "./plugins/highlightDecorationPlugin";
import {
  jsonContentToProseMirror,
  tagsToPositionedPhrases,
  getHighlightRenderData,
  filterVisibleTags,
  filterVisibleTeacherComments,
  type HighlightTagInput,
  type HighlightPhraseData,
  type JSONContent,
} from "./index";

export interface UnifiedMarkupViewerProps {
  /** Document content - accepts JSONContent or Record<string, unknown> from API */
  bodyJson: JSONContent | Record<string, unknown> | null | undefined;
  tags: ShareTag[];
  tagPalette: ShareTagDefinition[];
  annotatedDocument?: string | null;
  documentId?: string;
  assignmentId?: string;
  removedLabelIds?: string[] | null;
  documentStatus?: "draft" | "final";
  teacherComments?: TeacherComment[] | null;
  isTeacherView?: boolean;
  showAllMarkup?: boolean;
  completedHighlightIds?: string[] | null;
  selectedHighlightId?: string | null;
  showCompletedInDoc?: boolean;
  onDeselectHighlight?: () => void;
  hoveredLegendKey?: string | null;
}

const viewerContainerClass = "relative flex flex-col gap-4";
const viewerContentBaseClass =
  "flex flex-col gap-4 overflow-auto rounded-xl border border-theme-primary bg-white p-4 shadow-sm";
const emptyStateClass =
  "flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-hover bg-surface-secondary p-8 text-center text-theme-muted";
const tooltipWrapperClass = "fixed z-50 opacity-0 animate-fadeIn";
const tooltipCardClass =
  "pointer-events-auto relative flex min-w-[18rem] max-w-[20rem] flex-col gap-3 rounded-xl border border-theme-primary bg-white p-4 text-sm text-theme-secondary shadow-[0_8px_24px_rgba(15,23,42,0.08)]";
const hoverLockDelay = 250;

// Constants for selection tooltip
const SELECTION_TOOLTIP_ID = "SELECTION_TOOLTIP";
// eslint-disable-next-line no-restricted-syntax -- Teacher comment selection highlight color
const SELECTION_HIGHLIGHT_COLOR = "#64748b"; // slate-600

interface TooltipPosition {
  top: number;
  left: number;
  placement: "above" | "below";
  pending: boolean;
}

interface PositionedTooltip {
  highlight: HoveredHighlightExtended;
  position: TooltipPosition;
}

/** Extended highlight data including the full phrase data */
interface HoveredHighlightExtended extends HoveredHighlight {
  phraseData: HighlightPhraseData;
}

/**
 * Check if the current DOM selection intersects with any highlight decoration elements.
 */
function selectionIntersectsHighlightDOM(): boolean {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed) return false;

  try {
    const range = selection.getRangeAt(0);
    const editorRoot = document.querySelector('[data-testid="unified-markup-viewer"]');
    if (!editorRoot) return false;

    const highlights = editorRoot.querySelectorAll(".highlight-decoration");

    for (const highlight of Array.from(highlights)) {
      const highlightRange = document.createRange();
      try {
        highlightRange.selectNodeContents(highlight);
        if (range.compareBoundaryPoints(Range.END_TO_START, highlightRange) < 0) {
          continue;
        }
        if (range.compareBoundaryPoints(Range.START_TO_END, highlightRange) > 0) {
          continue;
        }
        return true;
      } finally {
        highlightRange.detach();
      }
    }

    return false;
  } catch (error) {
    console.warn("Error checking selection overlap:", error);
    return false;
  }
}

export function UnifiedMarkupViewer({
  bodyJson,
  tags,
  tagPalette,
  documentId,
  assignmentId,
  removedLabelIds,
  documentStatus = "draft",
  teacherComments = null,
  isTeacherView = false,
  showAllMarkup = false,
  completedHighlightIds = null,
  selectedHighlightId = null,
  showCompletedInDoc = true,
  onDeselectHighlight,
  hoveredLegendKey = null,
}: UnifiedMarkupViewerProps) {
  const editorRef = useRef<EditorRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const tooltipRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const selectionTooltipRef = useRef<HTMLDivElement | null>(null);

  // Hover state management
  const [hoveredHighlights, setHoveredHighlights] = useState<HoveredHighlightExtended[]>([]);
  const [positionedTooltips, setPositionedTooltips] = useState<PositionedTooltip[]>([]);
  const [selectionTooltipPosition, setSelectionTooltipPosition] = useState<TooltipPosition | null>(null);

  // Hover lock for smooth transitions
  const hoverTimeoutRef = useRef<number | null>(null);
  const isMouseOverTooltipRef = useRef<boolean>(false);
  const hoverLockIdsRef = useRef<Set<string> | null>(null);
  const lastHoveredHighlightsRef = useRef<HoveredHighlightExtended[]>([]);
  const pendingHighlightsRef = useRef<HoveredHighlightExtended[] | null>(null);
  const hoverPositionRafRef = useRef<number | null>(null);

  // Delete confirmation state
  const [deleteConfirmHighlightId, setDeleteConfirmHighlightId] = useState<string | null>(null);
  const [deleteConfirmCommentId, setDeleteConfirmCommentId] = useState<string | null>(null);

  // Teacher comment modals
  const [selectedText, setSelectedText] = useState<{
    startOffset: number;
    endOffset: number;
    phrase: string;
    beforeContext: string | null;
    afterContext: string | null;
    rect: DOMRect;
    containingPre?: string | null;
    containingSel?: string | null;
    containingPost?: string | null;
  } | null>(null);
  const [showAddCommentModal, setShowAddCommentModal] = useState(false);
  const [savedSelection, setSavedSelection] = useState<{
    startOffset: number;
    endOffset: number;
    phrase: string;
    beforeContext: string | null;
    afterContext: string | null;
    containingPre?: string | null;
    containingSel?: string | null;
    containingPost?: string | null;
  } | null>(null);
  const [editingComment, setEditingComment] = useState<TeacherComment | null>(null);

  const { show } = useNotification();
  const user = useAuthStore((state) => state.user);

  const completedHighlightIdSet = useMemo(
    () => new Set(completedHighlightIds || []),
    [completedHighlightIds]
  );

  // Convert document content to ProseMirror
  const proseMirrorDoc = useMemo(() => {
    if (!bodyJson) return null;
    try {
      // Cast TipTap's JSONContent to our stricter JSONContent type
      // Our adapter handles the case where type might be undefined
      return jsonContentToProseMirror(bodyJson as JSONContent);
    } catch (error) {
      console.error("Failed to convert document:", error);
      return null;
    }
  }, [bodyJson]);

  // Create set of removed label IDs
  const removedSet = useMemo(() => new Set(removedLabelIds || []), [removedLabelIds]);

  // Convert teacher comments to HighlightTagInput format
  const teacherCommentTags = useMemo((): HighlightTagInput[] => {
    if (!teacherComments) return [];
    return teacherComments.map((comment) => ({
      tag_id: `TEACHER_COMMENT_${comment.id}`,
      startPath: comment.startPath,
      startChar: comment.startChar,
      endPath: comment.endPath,
      endChar: comment.endChar,
      color: comment.color,
      label: `${comment.teacher_name} says:`,
      explanation: comment.comment_text,
      phrase: comment.phrase || "",
      beforeContext: comment.before_context || null,
      afterContext: comment.after_context || null,
      isTeacherComment: true,
      teacherCommentId: comment.id,
      teacherName: comment.teacher_name,
      createdBy: comment.created_by,
    }));
  }, [teacherComments]);

  // Filter visible tags
  const visibleTags = useMemo(() => {
    const highlightTags: HighlightTagInput[] = tags.map((tag) => ({
      tag_id: tag.tag_id,
      startPath: tag.startPath,
      startChar: tag.startChar,
      endPath: tag.endPath,
      endChar: tag.endChar,
      color: tag.color || null,
      label: tag.label || null,
      explanation: tag.explanation,
      suggestion: tag.suggestion || null,
      phrase: tag.phrase || null,
      beforeContext: tag.beforeContext || null,
      afterContext: tag.afterContext || null,
      display_style: tag.display_style,
    }));

    return filterVisibleTags(highlightTags, {
      removedSet,
      showAllMarkup,
      selectedHighlightId,
      hoveredLegendKey,
      isTeacherView,
      showCompletedInDoc,
      completedHighlightIdSet,
    });
  }, [
    tags,
    removedSet,
    showAllMarkup,
    selectedHighlightId,
    hoveredLegendKey,
    isTeacherView,
    showCompletedInDoc,
    completedHighlightIdSet,
  ]);

  // Filter visible teacher comments
  const visibleTeacherComments = useMemo(() => {
    return filterVisibleTeacherComments(teacherCommentTags, {
      selectedHighlightId,
      hoveredLegendKey,
      isTeacherView,
      showCompletedInDoc,
      completedHighlightIdSet,
    });
  }, [
    teacherCommentTags,
    selectedHighlightId,
    hoveredLegendKey,
    isTeacherView,
    showCompletedInDoc,
    completedHighlightIdSet,
  ]);

  // Create palette map
  const paletteMap = useMemo(() => {
    const map = new Map<string, ShareTagDefinition>();
    tagPalette.forEach((def) => map.set(def.key, def));
    return map;
  }, [tagPalette]);

  // Convert to positioned phrases when document or tags change
  const positionedPhrases = useMemo(() => {
    if (!proseMirrorDoc) {
      return [];
    }

    const allTags = [...visibleTags, ...visibleTeacherComments];

    return tagsToPositionedPhrases(
      proseMirrorDoc,
      allTags,
      tagPalette.map((p) => ({
        key: p.key,
        color: p.color,
        label: p.label,
        humanDescription: p.humanDescription,
      }))
    );
  }, [proseMirrorDoc, visibleTags, visibleTeacherComments, tagPalette]);

  // Close tooltip handler
  const closeTooltip = useCallback(() => {
    if (hoverTimeoutRef.current !== null) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredHighlights([]);
    setPositionedTooltips([]);
    isMouseOverTooltipRef.current = false;
    hoverLockIdsRef.current = null;
    pendingHighlightsRef.current = null;
  }, []);

  const selectedHighlightIdRef = useRef<string | null>(null);

  useEffect(() => {
    selectedHighlightIdRef.current = selectedHighlightId;
  }, [selectedHighlightId]);

  // Hover callback for highlight plugin
  const handleHighlightHover = useCallback((highlights: HoveredHighlight[]) => {
    // If navigating via sidebar, ignore hover events
    if (selectedHighlightIdRef.current) return;

    // Convert to extended highlights with phrase data
    const extendedHighlights: HoveredHighlightExtended[] = highlights.map((h) => ({
      ...h,
      phraseData: h.data.data as HighlightPhraseData,
    }));

    const nextHighlightIds = new Set(extendedHighlights.map((h) => h.id));

    if (hoverTimeoutRef.current !== null && hoverLockIdsRef.current !== null) {
      let intersectsLockedHighlight = false;
      nextHighlightIds.forEach((id) => {
        if (hoverLockIdsRef.current?.has(id)) {
          intersectsLockedHighlight = true;
        }
      });
      if (!intersectsLockedHighlight) {
        pendingHighlightsRef.current = extendedHighlights;
        return;
      }
    }

    if (hoverTimeoutRef.current !== null) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    hoverLockIdsRef.current = null;
    pendingHighlightsRef.current = null;
    setHoveredHighlights(extendedHighlights);
  }, []);

  // Leave callback for highlight plugin
  const handleHighlightLeave = useCallback(() => {
    if (selectedHighlightIdRef.current) return;

    const lockedIds = lastHoveredHighlightsRef.current.map((h) => h.id);
    hoverLockIdsRef.current = lockedIds.length > 0 ? new Set(lockedIds) : null;
    pendingHighlightsRef.current = null;

    if (hoverTimeoutRef.current !== null) {
      window.clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = window.setTimeout(() => {
      const pendingHighlights = pendingHighlightsRef.current;
      if (pendingHighlights && !isMouseOverTooltipRef.current) {
        pendingHighlightsRef.current = null;
        hoverLockIdsRef.current = null;
        setHoveredHighlights(pendingHighlights);
      } else if (!isMouseOverTooltipRef.current) {
        setHoveredHighlights([]);
        setPositionedTooltips([]);
        hoverLockIdsRef.current = null;
      }
      hoverTimeoutRef.current = null;
    }, hoverLockDelay);
  }, []);

  // Create plugins
  const plugins = useMemo((): Plugin[] => {
    return [
      createPositionedPhrasePlugin({
        defaultEditBehavior: "atomic",
      }),
      createHighlightDecorationPlugin({
        getRenderData: getHighlightRenderData,
        onHover: handleHighlightHover,
        onLeave: handleHighlightLeave,
      }),
    ];
  }, [handleHighlightHover, handleHighlightLeave]);

  // Update positioned phrases when they change
  useEffect(() => {
    const view = editorRef.current?.getView();
    if (!view) return;

    const tr = setPositionedPhrases(view.state, positionedPhrases);
    view.dispatch(tr);
  }, [positionedPhrases]);

  // Delete mutations
  const deleteHighlightMutation = useDeleteHighlight(
    assignmentId || "",
    documentId || ""
  );
  const deleteTeacherCommentMutation = useDeleteTeacherComment(
    assignmentId || "",
    documentId || ""
  );

  // Position tooltips when highlights change
  useLayoutEffect(() => {
    lastHoveredHighlightsRef.current = hoveredHighlights;

    if (hoverPositionRafRef.current !== null) {
      window.cancelAnimationFrame(hoverPositionRafRef.current);
      hoverPositionRafRef.current = null;
    }

    if (hoveredHighlights.length === 0) {
      setPositionedTooltips([]);
      // Clean up tooltip refs when no tooltips are visible
      tooltipRefs.current.clear();
      return;
    }

    // Clean up refs for tooltips that are no longer visible
    const currentHighlightIds = new Set(hoveredHighlights.map((h) => h.id));
    const keysToDelete: string[] = [];
    tooltipRefs.current.forEach((_, key) => {
      if (!currentHighlightIds.has(key)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => tooltipRefs.current.delete(key));

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const margin = 12;
    const tooltipSpacing = 8;

    const calculatePositions = (
      highlights: HoveredHighlightExtended[]
    ): { positioned: PositionedTooltip[]; missingTooltipDimensions: boolean } => {
      const positioned: PositionedTooltip[] = [];
      let missingTooltipDimensions = false;

      const tooltipDimensions = highlights.map((highlight) => {
        const tooltipElement = tooltipRefs.current.get(highlight.id);
        let width = tooltipElement?.offsetWidth ?? 0;
        let height = tooltipElement?.offsetHeight ?? 0;
        let fallback = false;

        if (width < 1 || height < 1) {
          fallback = true;
          missingTooltipDimensions = true;
          width = 288;
          height = 160;
        }

        return { width, height, fallback };
      });

      const createPosition = (
        highlight: HoveredHighlightExtended,
        top: number,
        left: number,
        placement: "above" | "below",
        fallback: boolean
      ) => {
        positioned.push({
          highlight,
          position: { top, left, placement, pending: fallback },
        });
      };

      if (highlights.length === 1) {
        const highlight = highlights[0];
        const rect = highlight.rect;
        const { width: tooltipWidth, height: tooltipHeight, fallback } = tooltipDimensions[0];

        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        let placement: "above" | "below" = "below";
        let top = rect.bottom + margin;

        if (spaceBelow < tooltipHeight + margin && spaceAbove > tooltipHeight + margin) {
          placement = "above";
          top = rect.top - tooltipHeight - margin;
        }

        let left = rect.left + rect.width / 2 - tooltipWidth / 2;
        left = Math.min(Math.max(left, margin), viewportWidth - tooltipWidth - margin);

        createPosition(highlight, top, left, placement, fallback);
      } else {
        // Multiple highlights - stack vertically
        let accumulatedHeight = 0;
        const anchorRect = highlights[0].rect;
        const spaceBelow = viewportHeight - anchorRect.bottom;
        const spaceAbove = anchorRect.top;
        const preferBelow = spaceBelow > spaceAbove;

        highlights.forEach((highlight, i) => {
          const { width, height, fallback } = tooltipDimensions[i];

          const placement: "above" | "below" = preferBelow ? "below" : "above";
          const top =
            placement === "below"
              ? anchorRect.bottom + margin + accumulatedHeight
              : anchorRect.top - height - margin - accumulatedHeight;

          let left = anchorRect.left + anchorRect.width / 2 - width / 2;
          left = Math.min(Math.max(left, margin), viewportWidth - width - margin);

          createPosition(highlight, top, left, placement, fallback);
          accumulatedHeight += height + tooltipSpacing;
        });
      }

      return { positioned, missingTooltipDimensions };
    };

    const { positioned, missingTooltipDimensions } = calculatePositions(hoveredHighlights);
    setPositionedTooltips(positioned);

    if (missingTooltipDimensions) {
      hoverPositionRafRef.current = window.requestAnimationFrame(() => {
        hoverPositionRafRef.current = null;
        const latestHoverHighlights = lastHoveredHighlightsRef.current;
        if (latestHoverHighlights.length === 0) {
          setPositionedTooltips([]);
          return;
        }
        const recalculated = calculatePositions(latestHoverHighlights);
        setPositionedTooltips(recalculated.positioned);
      });
    }

    return () => {
      if (hoverPositionRafRef.current !== null) {
        window.cancelAnimationFrame(hoverPositionRafRef.current);
        hoverPositionRafRef.current = null;
      }
    };
  }, [hoveredHighlights]);

  // Sync tooltip positions on scroll (throttled with requestAnimationFrame)
  useEffect(() => {
    const container = editorContainerRef.current;
    if (!container) return;

    let rafId: number | null = null;

    const handleScroll = () => {
      if (positionedTooltips.length === 0) return;

      // Throttle to animation frame rate (~60fps max, but only one pending)
      if (rafId !== null) return;

      rafId = window.requestAnimationFrame(() => {
        rafId = null;

        setPositionedTooltips((prev) => {
          if (prev.length === 0) return prev;

          const updated: PositionedTooltip[] = [];

          prev.forEach(({ highlight, position }) => {
            const el = container.querySelector(
              `[data-highlight-id="${highlight.id}"]`
            );

            if (!el) {
              updated.push({ highlight, position });
              return;
            }

            const rect = el.getBoundingClientRect();
            const deltaY = rect.top - highlight.rect.top;

            const updatedHighlight: HoveredHighlightExtended = {
              ...highlight,
              rect: new DOMRect(rect.left, rect.top, rect.width, rect.height),
            };

            updated.push({
              highlight: updatedHighlight,
              position: {
                ...position,
                top: position.top + deltaY,
              },
            });
          });

          lastHoveredHighlightsRef.current = updated.map((pt) => pt.highlight);
          return updated;
        });
      });
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      container.removeEventListener("scroll", handleScroll);
    };
  }, [positionedTooltips.length]);

  // Position selection tooltip
  useLayoutEffect(() => {
    if (!selectedText || !isTeacherView || !assignmentId || !documentId || !bodyJson) {
      setSelectionTooltipPosition(null);
      return;
    }

    if (selectionIntersectsHighlightDOM()) {
      setSelectionTooltipPosition(null);
      return;
    }

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const margin = 12;

    const measureAndPosition = () => {
      const rect = selectedText.rect;
      const el = selectionTooltipRef.current;
      const tooltipWidth = el?.offsetWidth ?? 288;
      const tooltipHeight = el?.offsetHeight ?? 160;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      let placement: "above" | "below" = "below";
      let top = rect.bottom + margin;
      if (spaceBelow < tooltipHeight + margin && spaceAbove > tooltipHeight + margin) {
        placement = "above";
        top = rect.top - tooltipHeight - margin;
      }

      let left = rect.left + rect.width / 2 - tooltipWidth / 2;
      left = Math.min(Math.max(left, margin), viewportWidth - tooltipWidth - margin);

      setSelectionTooltipPosition({ top, left, placement, pending: false });
    };

    measureAndPosition();
    const raf = window.requestAnimationFrame(() => measureAndPosition());
    return () => window.cancelAnimationFrame(raf);
  }, [selectedText, isTeacherView, assignmentId, documentId, bodyJson]);

  // Text selection tracking for teacher comments
  useEffect(() => {
    if (!isTeacherView || !assignmentId || !documentId || !bodyJson) return;

    const handleSelectionChange = () => {
      const selection = window.getSelection();

      if (!selection || selection.isCollapsed) {
        setSelectedText((prev) => (prev === null ? null : null));
        return;
      }

      const editorRoot = editorContainerRef.current?.querySelector(".ProseMirror");
      if (!editorRoot) {
        setSelectedText(null);
        return;
      }

      const { anchorNode, focusNode } = selection;
      if (!anchorNode || !focusNode) {
        setSelectedText(null);
        return;
      }
      if (!editorRoot.contains(anchorNode) || !editorRoot.contains(focusNode)) {
        setSelectedText(null);
        return;
      }

      const range = selection.getRangeAt(0);
      const selectedPhrase = range.toString().trim();
      if (!selectedPhrase) {
        setSelectedText(null);
        return;
      }

      // Get plain text and calculate offsets
      const plainText = getPlainTextFromTipTap(bodyJson as Record<string, unknown>);

      // Calculate offsets from the selection
      const view = editorRef.current?.getView();
      if (!view) {
        setSelectedText(null);
        return;
      }

      // Get ProseMirror positions from DOM selection
      const anchorPos = view.posAtDOM(anchorNode, selection.anchorOffset);
      const focusPos = view.posAtDOM(focusNode, selection.focusOffset);
      const pmStartPos = Math.min(anchorPos, focusPos);
      const pmEndPos = Math.max(anchorPos, focusPos);

      if (pmStartPos === -1 || pmEndPos === -1) {
        setSelectedText(null);
        return;
      }

      // Convert ProseMirror positions to plain text offsets
      const doc = view.state.doc;
      const startOffset = pmPositionToPlainTextOffset(doc, pmStartPos);
      const endOffset = pmPositionToPlainTextOffset(doc, pmEndPos);

      const context = extractContext(plainText, startOffset, endOffset);
      const containingInfo = getContainingSentenceInfo(plainText, startOffset, endOffset);
      let containingPre: string | null = null;
      let containingSel: string | null = null;
      let containingPost: string | null = null;
      if (containingInfo) {
        const relStart = Math.max(0, startOffset - containingInfo.sentenceStart);
        const relEnd = Math.max(
          relStart,
          Math.min(containingInfo.sentence.length, endOffset - containingInfo.sentenceStart)
        );
        containingPre = containingInfo.sentence.slice(0, relStart);
        containingSel = containingInfo.sentence.slice(relStart, relEnd);
        containingPost = containingInfo.sentence.slice(relEnd);
      }

      const rect = range.getBoundingClientRect();

      setSelectedText((prev) => {
        if (
          prev &&
          prev.startOffset === startOffset &&
          prev.endOffset === endOffset &&
          prev.phrase === context.phrase
        ) {
          return prev;
        }
        return {
          startOffset,
          endOffset,
          phrase: context.phrase,
          beforeContext: context.beforeContext,
          afterContext: context.afterContext,
          rect,
          containingPre,
          containingSel,
          containingPost,
        };
      });
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, [bodyJson, isTeacherView, assignmentId, documentId]);

  // Show tooltip for selected highlight (from sidebar navigation)
  useEffect(() => {
    if (!selectedHighlightId || !proseMirrorDoc) return;

    // Clear any pending hover state
    if (hoverTimeoutRef.current !== null) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    hoverLockIdsRef.current = null;
    pendingHighlightsRef.current = null;
    isMouseOverTooltipRef.current = false;

    const showTooltip = () => {
      const highlightElement = document.querySelector(
        `[data-highlight-id="${selectedHighlightId}"]`
      ) as HTMLElement;

      if (!highlightElement) {
        console.warn("[UnifiedMarkupViewer] Highlight element not found for ID:", selectedHighlightId);
        return;
      }

      // Find the positioned phrase
      const phrase = positionedPhrases.find((p) => p.id === selectedHighlightId);
      if (!phrase) {
        console.warn("[UnifiedMarkupViewer] Positioned phrase not found");
        return;
      }

      const phraseData = phrase.data as HighlightPhraseData;
      const rect = highlightElement.getBoundingClientRect();

      const hoveredHighlight: HoveredHighlightExtended = {
        id: phrase.id,
        rect: new DOMRect(rect.left, rect.top, rect.width, rect.height),
        data: phrase,
        color: phraseData.color,
        label: phraseData.label,
        phraseData,
      };

      setHoveredHighlights([hoveredHighlight]);
    };

    const timeoutId = setTimeout(showTooltip, 50);
    return () => clearTimeout(timeoutId);
  }, [selectedHighlightId, positionedPhrases, proseMirrorDoc]);

  // Click handler to deselect highlight
  const handleDocumentClick = useCallback(
    (e: React.MouseEvent) => {
      if (!selectedHighlightId || !onDeselectHighlight) return;

      const target = e.target as HTMLElement;
      if (target.closest(".highlight-decoration")) return;

      closeTooltip();
      onDeselectHighlight();
    },
    [selectedHighlightId, onDeselectHighlight, closeTooltip]
  );

  const containerClass = `${viewerContainerClass} h-full min-h-0`;
  const viewerContentClass = `${viewerContentBaseClass} h-full min-h-0`;

  if (!bodyJson) {
    return (
      <div
        className={emptyStateClass}
        data-testid="unified-markup-viewer"
        data-readonly="true"
      >
        <p className={mutedText}>{ui.empty.noData}</p>
      </div>
    );
  }

  if (!proseMirrorDoc) {
    return (
      <div
        className={emptyStateClass}
        data-testid="unified-markup-viewer"
        data-readonly="true"
      >
        {/* eslint-disable-next-line no-restricted-syntax -- Error state for invalid document */}
        <p className={mutedText}>Failed to load document</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={containerClass}>
      <div
        ref={editorContainerRef}
        data-testid="unified-markup-viewer"
        data-readonly="true"
        className={viewerContentClass}
        onClick={handleDocumentClick}
      >
        <Editor
          ref={editorRef}
          initialContent={proseMirrorDoc}
          editable={false}
          plugins={plugins}
          className="min-h-[260px] space-y-4 text-base leading-6 text-theme-primary focus:outline-none"
        />
      </div>

      {/* Selection tooltip */}
      {selectionTooltipPosition && (
        <div
          ref={selectionTooltipRef}
          role="tooltip"
          data-tag-id={SELECTION_TOOLTIP_ID}
          className={tooltipWrapperClass}
          style={{ top: selectionTooltipPosition.top, left: selectionTooltipPosition.left }}
        >
          <div className={tooltipCardClass}>
            <span
              className="h-2 w-10 rounded-full"
              style={{ backgroundColor: SELECTION_HIGHLIGHT_COLOR }}
            />
            <div className="flex flex-col gap-3">
              <span className="text-sm font-semibold text-brand-900">
                {ui.tooltips.addOwnComment}
              </span>
              <p className="text-sm text-theme-secondary">{ui.tooltips.addCommentHelp}</p>
            </div>
            <div className="flex justify-center pt-4 pb-3">
              <button
                type="button"
                className={buttonVariants.primary}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (selectedText) {
                    setSavedSelection({
                      startOffset: selectedText.startOffset,
                      endOffset: selectedText.endOffset,
                      phrase: selectedText.phrase,
                      beforeContext: selectedText.beforeContext,
                      afterContext: selectedText.afterContext,
                      containingPre: selectedText.containingPre ?? null,
                      containingSel: selectedText.containingSel ?? null,
                      containingPost: selectedText.containingPost ?? null,
                    });
                  }
                  setShowAddCommentModal(true);
                  window.getSelection()?.removeAllRanges();
                  setSelectedText(null);
                  setSelectionTooltipPosition(null);
                }}
              >
                {ui.actions.add} {ui.labels.comment}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Positioned tooltips */}
      {positionedTooltips.map(({ highlight, position }) => {
        const isCompleted = completedHighlightIdSet.has(highlight.id);
        const phraseData = highlight.phraseData;
        const paletteEntry = paletteMap.get(phraseData.tag_id);
        const activeDescription = phraseData.humanDescription ?? paletteEntry?.humanDescription ?? null;

        return (
          <div
            key={highlight.id}
            ref={(el) => {
              if (el) tooltipRefs.current.set(highlight.id, el);
            }}
            role="tooltip"
            data-tag-id={phraseData.tag_id}
            className={tooltipWrapperClass}
            style={{
              top: position.top,
              left: position.left,
              visibility: position.pending ? "hidden" : undefined,
            }}
            onMouseEnter={() => {
              if (selectedHighlightId) return;
              if (hoverTimeoutRef.current !== null) {
                window.clearTimeout(hoverTimeoutRef.current);
                hoverTimeoutRef.current = null;
              }
              isMouseOverTooltipRef.current = true;
              hoverLockIdsRef.current = null;
              pendingHighlightsRef.current = null;
            }}
            onMouseLeave={() => {
              if (selectedHighlightId) return;
              isMouseOverTooltipRef.current = false;
              hoverLockIdsRef.current = null;
              pendingHighlightsRef.current = null;
              setHoveredHighlights([]);
            }}
          >
            <div className={tooltipCardClass}>
              {isTeacherView && isCompleted && (
                <div className="mb-1 flex items-center gap-2">
                  <Tooltip content={ui.highlightNavigation.resolvedTooltip}>
                    <span className="inline-flex items-center">
                      <PiCheckTickCircleSolid size={16} className="text-semantic-success" />
                    </span>
                  </Tooltip>
                  <span className="text-xs font-medium text-semantic-success-text">
                    {ui.highlightNavigation.completedIcon}
                  </span>
                </div>
              )}

              {/* Edit/Delete buttons for teacher comments and AI highlights */}
              {documentId && assignmentId && isTeacherView && (
                <>
                  {/* Edit button (only for teacher comments created by current user) */}
                  {phraseData.isTeacherComment && phraseData.createdBy === user?.id && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const comment = teacherComments?.find(
                          (c) => c.id === phraseData.teacherCommentId
                        );
                        if (comment) setEditingComment(comment);
                      }}
                      className="absolute right-11 top-3 flex h-6 w-6 items-center justify-center rounded-md text-theme-muted transition-colors hover:bg-primary-light hover:text-primary"
                      title="Edit comment"
                      aria-label="Edit comment"
                    >
                      <PiPencilEditStroke className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}

                  {/* Delete button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (phraseData.isTeacherComment) {
                        setDeleteConfirmCommentId(phraseData.teacherCommentId || null);
                      } else {
                        setDeleteConfirmHighlightId(highlight.id);
                      }
                    }}
                    disabled={
                      (deleteHighlightMutation.isPending &&
                        deleteConfirmHighlightId === highlight.id) ||
                      (deleteTeacherCommentMutation.isPending &&
                        deleteConfirmCommentId === phraseData.teacherCommentId)
                    }
                    className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-md text-theme-muted transition-colors hover:bg-semantic-error-light hover:text-semantic-error disabled:opacity-50"
                    title={phraseData.isTeacherComment ? "Delete comment" : "Remove highlight"}
                    aria-label={phraseData.isTeacherComment ? "Delete comment" : "Remove highlight"}
                  >
                    {(deleteHighlightMutation.isPending &&
                      deleteConfirmHighlightId === highlight.id) ||
                    (deleteTeacherCommentMutation.isPending &&
                      deleteConfirmCommentId === phraseData.teacherCommentId) ? (
                      <Spinner size="sm" />
                    ) : (
                      <PiDeleteDustbin01Stroke className="h-4 w-4" aria-hidden="true" />
                    )}
                  </button>
                </>
              )}

              <span
                className="h-2 w-10 rounded-full"
                style={{ backgroundColor: highlight.color }}
              />
              <div className="flex flex-col gap-3">
                <span className="text-sm font-semibold text-brand-900 pr-6">{highlight.label}</span>
                {activeDescription && (
                  <span className="text-xs uppercase tracking-wide text-theme-muted">
                    {activeDescription}
                  </span>
                )}
                <p className="text-sm text-theme-secondary">{phraseData.explanation}</p>
                {documentStatus === "final" && phraseData.suggestion && (
                  <span className="rounded-lg bg-semantic-plan-bg px-3 py-2 text-sm text-semantic-plan-text">
                    {phraseData.suggestion}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Add Comment Modal */}
      {savedSelection && bodyJson && (
        <AddCommentModal
          isOpen={showAddCommentModal}
          onClose={() => {
            setShowAddCommentModal(false);
            setSavedSelection(null);
            window.getSelection()?.removeAllRanges();
            setSelectedText(null);
          }}
          assignmentId={assignmentId!}
          documentId={documentId!}
          bodyJson={bodyJson}
          selection={savedSelection}
          teacherName={user?.fullName || "Teacher"}
        />
      )}

      {/* Edit Comment Modal */}
      {editingComment && (
        <EditCommentModal
          isOpen={!!editingComment}
          onClose={() => {
            setEditingComment(null);
            closeTooltip();
          }}
          assignmentId={assignmentId!}
          documentId={documentId!}
          comment={editingComment}
        />
      )}

      {/* Delete AI highlight confirmation modal */}
      <Modal
        isOpen={deleteConfirmHighlightId !== null}
        onClose={() => setDeleteConfirmHighlightId(null)}
      >
        <ModalHeader
          title={`${ui.actions.confirm} ${ui.actions.delete}`}
          onClose={() => setDeleteConfirmHighlightId(null)}
        />
        <ModalBody>
          <p>{confirmations.highlights?.delete || confirmations.generic.archive("highlight", "")}</p>
        </ModalBody>
        <ModalFooter>
          <button
            className={buttonVariants.secondary}
            onClick={() => setDeleteConfirmHighlightId(null)}
          >
            {ui.actions.cancel}
          </button>
          <button
            className={buttonVariants.danger}
            onClick={() => {
              if (deleteConfirmHighlightId) {
                deleteHighlightMutation.mutate(deleteConfirmHighlightId, {
                  onSuccess: () => {
                    setDeleteConfirmHighlightId(null);
                    closeTooltip();
                  },
                  onError: () => {
                    setDeleteConfirmHighlightId(null);
                    const TOAST_ERROR: NotificationKind = "error";
                    show(notifications.highlights.removeFailed, TOAST_ERROR);
                  },
                });
              }
            }}
            disabled={deleteHighlightMutation.isPending}
          >
            {deleteHighlightMutation.isPending ? ui.actions.deleting : ui.actions.delete}
          </button>
        </ModalFooter>
      </Modal>

      {/* Delete teacher comment confirmation modal */}
      <Modal
        isOpen={deleteConfirmCommentId !== null}
        onClose={() => setDeleteConfirmCommentId(null)}
      >
        <ModalHeader
          title={`${ui.actions.confirm} ${ui.actions.delete}`}
          onClose={() => setDeleteConfirmCommentId(null)}
        />
        <ModalBody>
          <p>
            {confirmations.highlights?.deleteComment || confirmations.generic.archive("comment", "")}
          </p>
        </ModalBody>
        <ModalFooter>
          <button
            className={buttonVariants.secondary}
            onClick={() => setDeleteConfirmCommentId(null)}
          >
            {ui.actions.cancel}
          </button>
          <button
            className={buttonVariants.danger}
            onClick={() => {
              if (deleteConfirmCommentId) {
                deleteTeacherCommentMutation.mutate(deleteConfirmCommentId, {
                  onSuccess: () => {
                    setDeleteConfirmCommentId(null);
                    closeTooltip();
                  },
                  onError: () => {
                    setDeleteConfirmCommentId(null);
                    const TOAST_ERROR: NotificationKind = "error";
                    show(notifications.highlights.deleteCommentFailed, TOAST_ERROR);
                  },
                });
              }
            }}
            disabled={deleteTeacherCommentMutation.isPending}
          >
            {deleteTeacherCommentMutation.isPending ? ui.actions.deleting : ui.actions.delete}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
